// The merge requests of the game (spec/dashboard.md section 12.3; spec/api-conventions.md section 12.2): the filters live in
// the URL (`?state=` — open by default, `all` sends none —, `?source=`, `?target=`), `GET …/merge-requests` newest first
// with Load more, a row links to the request page; New merge request… opens the create dialog. The Comments column shows
// the request's `comments.total` with the unresolved count as a warning badge (api-conventions section 12.2 "Comments").
import { useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { ops } from '../../api/operations';
import type { MergeRequest, Page } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { DataGrid, type Column } from '../../ui/DataGrid';
import { Field } from '../../ui/Field';
import { AlertCircle, Check, GitPullRequest, RefreshCw } from '../../ui/icons';
import { Select } from '../../ui/inputs';
import { formatInstant, useTimeZone, zoneLabel } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { queryCache, useQuery } from '../../workspace/cache';
import { asApiError, currentApprovals, mrPrefix, PAGE_SIZE, plural, STATE_TONE, STATES, type StateFilter } from './merge';
import { NewMergeRequestDialog } from './NewMergeRequestDialog';
import { commentCounts } from './Conversation';
import './merge.css';
import { EmptyState } from '../../ui/Banner';

export { MergeRequestPage } from './MergeRequestPage';

export function MergeRequestsPage() {
  const { branch = '' } = useParams();
  const { org, game, branches, can, offsets } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const [zone] = useTimeZone();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);

  const stateParam = params.get('state');
  const state: StateFilter = (STATES as readonly string[]).includes(stateParam ?? '') ? (stateParam as StateFilter) : 'open';
  const source = params.get('source') ?? '';
  const target = params.get('target') ?? '';
  const query = { ...(state !== 'all' ? { state } : {}), ...(source ? { source } : {}), ...(target ? { target } : {}) };
  const prefix = mrPrefix(org, game.slug);
  const listKey = `${prefix}:list?state=${state}&source=${encodeURIComponent(source)}&target=${encodeURIComponent(target)}`;
  const fetchPage = (cursor: string | null) =>
    client.data(ops.listMergeRequests, { path: { org, game: game.slug }, query: { ...query, limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) } }) as unknown as Promise<Page<MergeRequest>>;
  const listQ = useQuery(listKey, () => fetchPage(null));
  const first = listQ.data as Page<MergeRequest> | undefined;

  // the pages after the first: valid only for the first page they were loaded after (a filter change or a re-read drops them)
  const [more, setMore] = useState<{ base: Page<MergeRequest>; items: MergeRequest[]; cursor: string | null } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const firstRef = useRef(first);
  firstRef.current = first;
  const extra = more && more.base === first ? more : null;
  const rows: MergeRequest[] = [...(first?.items ?? []), ...(extra?.items ?? [])];
  const nextCursor = extra ? extra.cursor : (first?.nextCursor ?? null);

  const loadMore = async () => {
    if (!first || !nextCursor || loadingMore) return;
    const base = first;
    setLoadingMore(true);
    try {
      const page = await fetchPage(nextCursor);
      if (firstRef.current !== base) return;
      setMore({ base, items: [...(extra?.items ?? []), ...(page.items ?? [])], cursor: page.nextCursor ?? null });
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.message, { requestId: err.requestId });
    } finally {
      setLoadingMore(false);
    }
  };

  const setFilter = (key: 'state' | 'source' | 'target', value: string) => {
    const next = new URLSearchParams(params);
    if (value && !(key === 'state' && value === 'open')) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const reload = () => queryCache.invalidate(`${prefix}:list`);

  const when = (v: string | null | undefined) => formatInstant(v, zone, { offsets });
  const columns: Column<MergeRequest>[] = [
    {
      key: 'number',
      header: '!',
      fixed: true,
      width: 70,
      render: (m) => <Link className="mono" to={routes.mergeRequest(org, game.slug, branch, m.number)}>!{String(m.number)}</Link>,
      cellText: (m) => `!${m.number}`,
      sortValue: (m) => Number(m.number),
    },
    { key: 'title', header: 'Title', render: (m) => m.title, cellText: (m) => m.title, sortValue: (m) => m.title },
    {
      key: 'branches',
      header: 'Source → target',
      render: (m) => (
        <span className="mr-branches">
          <code>{m.sourceBranch}</code> → <code>{m.targetBranch}</code>
        </span>
      ),
      cellText: (m) => `${m.sourceBranch} → ${m.targetBranch}`,
    },
    { key: 'state', header: 'State', render: (m) => <Badge tone={STATE_TONE[m.state ?? ''] ?? 'neutral'}>{m.state}</Badge>, cellText: (m) => String(m.state), sortValue: (m) => String(m.state) },
    { key: 'changes', header: 'Changes', align: 'right', render: (m) => String(m.changes), cellText: (m) => String(m.changes), sortValue: (m) => Number(m.changes) },
    { key: 'unresolved', header: 'Unresolved', align: 'right', render: (m) => String(m.unresolved), cellText: (m) => String(m.unresolved), sortValue: (m) => Number(m.unresolved) },
    { key: 'approvals', header: 'Approvals', align: 'right', render: (m) => String(currentApprovals(m)), cellText: (m) => String(currentApprovals(m)), sortValue: (m) => currentApprovals(m) },
    {
      key: 'comments',
      header: 'Comments',
      align: 'right',
      render: (m) => {
        const c = commentCounts(m);
        return (
          <span className="mr-comments-cell" data-testid={`mr-comments-${m.number}`}>
            {String(c.total)}
            {c.unresolved > 0 && (
              <>
                {' '}
                <Badge tone="warning">{c.unresolved} unresolved</Badge>
              </>
            )}
          </span>
        );
      },
      cellText: (m) => {
        const c = commentCounts(m);
        return c.unresolved > 0 ? `${c.total} (${c.unresolved} unresolved)` : String(c.total);
      },
      sortValue: (m) => commentCounts(m).total,
    },
    {
      key: 'mergeable',
      header: 'Mergeable',
      render: (m) => <span aria-label={m.mergeable ? 'mergeable' : 'not mergeable'}>{m.mergeable ? <Check className="icon-s" aria-hidden="true" /> : '—'}</span>,
      cellText: (m) => (m.mergeable ? 'yes' : '—'),
    },
    { key: 'author', header: 'Author', render: (m) => m.createdBy?.label ?? '—', cellText: (m) => m.createdBy?.label ?? '' },
    { key: 'updated', header: 'Updated', render: (m) => when(m.updatedAt), cellText: (m) => when(m.updatedAt), sortValue: (m) => m.updatedAt ?? '' },
  ];

  const error = listQ.error ? asApiError(listQ.error) : null;
  const total = first?.total;
  const count = total !== null && total !== undefined && total !== '' ? total : rows.length;
  const filtered = state !== 'open' || source !== '' || target !== '';
  return (
    <section className="mr-page" data-testid="merge-requests">
      <div className="page-head">
        <h1>Merge requests</h1>
        <div className="spacer" />
        <Button variant="primary" icon={<GitPullRequest aria-hidden="true" />} onClick={() => setCreating(true)} missingPermission={can('content_write') ? null : 'content_write'}>
          New merge request…
        </Button>
      </div>
      <form className="page-toolbar mr-toolbar" role="search" aria-label="Merge request filters" onSubmit={(e) => e.preventDefault()}>
        <div className="form-grid mr-filters">
        <Field label="State">
          <Select size="s" aria-label="State" value={state} onChange={(e) => setFilter('state', e.target.value)}>
            {STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </Field>
        <BranchFilter label="Source" value={source} onChange={(v) => setFilter('source', v)} names={branches.map((b) => b.name)} />
        <BranchFilter label="Target" value={target} onChange={(v) => setFilter('target', v)} names={branches.map((b) => b.name)} />
        </div>
      </form>
      {error && (
        <div className="banner banner-danger" role="alert">
          <AlertCircle className="banner-icon" aria-hidden="true" />
          <div className="banner-body">
            {error.message}
            {error.requestId ? ` (request ${error.requestId})` : ''}
          </div>
          <Button size="s" onClick={reload}>
            Retry
          </Button>
        </div>
      )}
      <DataGrid
        id="merge-requests"
        columns={columns}
        rows={rows}
        rowKey={(m) => m.id}
        loading={listQ.loading && !first}
        emptyState={<EmptyState icon={<GitPullRequest />} title={filtered ? 'No merge request matches these filters' : 'No open merge requests'} body={filtered ? 'Widen or clear the filters to see more requests.' : 'A merge request brings the changes of one branch into another after review.'} />}
        caption={
          <span data-testid="mr-count">
            {plural(count, 'merge request')}
            {total === null || total === undefined ? ' loaded' : ''} <span className="muted small">· times in {zoneLabel(zone, { offsets })}</span>
          </span>
        }
        toolbar={
          <>
            <div className="spacer" />
            <Button size="s" icon={<RefreshCw aria-hidden="true" />} onClick={reload} busy={listQ.loading && !!first}>
              Refresh
            </Button>
          </>
        }
      />
      {nextCursor && (
        <div className="mr-more">
          <span className="muted small">{rows.length} loaded, more available</span>
          <Button size="s" data-testid="mr-load-more" onClick={() => void loadMore()} busy={loadingMore}>
            Load more
          </Button>
        </div>
      )}
      {creating && <NewMergeRequestDialog branch={branch} onClose={() => setCreating(false)} />}
    </section>
  );
}

function BranchFilter({ label, value, onChange, names }: { label: string; value: string; onChange: (v: string) => void; names: string[] }) {
  return (
    <Field label={label}>
      <Select size="s" aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">any</option>
        {value && !names.includes(value) && <option value={value}>{value}</option>}
        {names.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </Select>
    </Field>
  );
}
